/**
 * A list read page by page in the platform's order: the first page on load, the next only when the planner asks for it
 * (`next_cursor`), never the whole list (`docs/adr/9076`). «Perché › Non inviate» was the first (#468); the screens that
 * followed it share this hook rather than each keeping a copy.
 *
 * The pages asked for belong to one question — the run, the ranking and the filters, `signature` — and are forgotten
 * when it changes: a cursor is a position in ONE ordering, and the next page of another is not this one's.
 */

import { useCallback, useMemo, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { useApiQueries, type OperationDescriptor, type ParamsOf, type ResultOf } from '../api';

/** The page fields every cursor-paged read answers (`CursorPage`, ADR 9018). */
export interface CursorAnswer<I> {
  readonly items: readonly I[];
  readonly has_more?: boolean;
  readonly next_cursor?: string | null;
}

export interface CursorPages<S extends OperationDescriptor, I> {
  /** The first page's read: its answer carries the totals of every page. */
  readonly first: UseQueryResult<ResultOf<S>, Error>;
  /** Every row of the pages read, in order. */
  readonly rows: readonly I[];
  /** The answers of the pages read, in order (`undefined` while one is being read): what a page says beside its rows. */
  readonly answers: readonly (ResultOf<S> | undefined)[];
  /** The cursor of the page after the last one read; `null` when there is none. */
  readonly next: string | null;
  /** Read the next page. Does nothing without one. */
  readonly loadMore: () => void;
  /** A page after the first is being read. */
  readonly loadingMore: boolean;
}

const NO_CURSORS: readonly string[] = [];

/** `items`, the same array as the last render's while every element is the same: a stable memo input. */
function useSameItems<T>(items: readonly T[]): readonly T[] {
  const [held, setHeld] = useState(items);
  const same = held.length === items.length && held.every((item, index) => item === items[index]);
  if (!same) setHeld(items);
  return same ? held : items;
}

/**
 * The pages of `operation` read so far. `params(cursor)` is the request of one page — `null` the first — and
 * `signature` names the question all of them answer.
 */
export function useCursorPages<S extends OperationDescriptor, I>(
  operation: S,
  signature: string,
  params: (cursor: string | null) => ParamsOf<S>,
  options: { readonly enabled?: boolean } = {},
): CursorPages<S, I> {
  const [asked, setAsked] = useState<{ readonly signature: string; readonly cursors: readonly string[] }>({ signature, cursors: [] });
  const cursors = asked.signature === signature ? asked.cursors : NO_CURSORS;
  // One request per page asked for; the query client keys each by its parameters, so a render reads nothing again.
  const requests = [null, ...cursors].map((cursor) => params(cursor));
  const pages = useApiQueries(operation, requests, options.enabled === undefined ? {} : { enabled: options.enabled });
  const first = pages[0]!;
  const last = pages[pages.length - 1]!;
  const answers = useSameItems(pages.map((page) => page.data as CursorAnswer<I> | undefined));
  const rows: readonly I[] = useMemo(() => answers.flatMap((answer) => answer?.items ?? []), [answers]);
  const lastAnswer = last.data as CursorAnswer<I> | undefined;
  const next = lastAnswer?.has_more === true ? lastAnswer.next_cursor ?? null : null;
  const loadMore = useCallback(() => {
    if (next === null) return;
    setAsked({ signature, cursors: [...cursors, next] });
  }, [next, signature, cursors]);
  return { first, rows, answers: answers as readonly (ResultOf<S> | undefined)[], next, loadMore, loadingMore: pages.length > 1 && last.isPending };
}
